<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { AlertTriangle, Mail } from "lucide-vue-next"
import { useI18n } from "@/composables/useI18n"

const props = withDefaults(
  defineProps<{
    open: boolean
    canDismiss?: boolean
  }>(),
  { canDismiss: false }
)

const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()

function handleOpenChange(v: boolean) {
  if (!v) emit("close")
}

function onPointerDownOutside(e: { preventDefault(): void }) {
  if (!props.canDismiss) e.preventDefault()
}

function onEscapeKeyDown(e: { preventDefault(): void }) {
  if (!props.canDismiss) e.preventDefault()
}
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent
      :class="[canDismiss ? '' : '[&>button]:hidden', 'max-w-md']"
      @pointer-down-outside="onPointerDownOutside"
      @escape-key-down="onEscapeKeyDown"
    >
      <DialogHeader class="text-center space-y-4 mb-6">
        <div class="flex flex-col items-center justify-center gap-4">
          <div
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 ring-4 ring-amber-500/10"
          >
            <AlertTriangle class="h-6 w-6" />
          </div>
          <div class="space-y-1 text-center">
            <DialogTitle class="text-lg">
              {{ t("common.trialExpiredModal.title") }}
            </DialogTitle>
            <DialogDescription class="text-base">
              {{ t("common.trialExpiredModal.subtitle") }}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div
        class="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground text-center"
      >
        <p class="leading-relaxed">
          {{ t("common.trialExpiredModal.description") }}
        </p>
        <a
          href="mailto:contact@dinecloud.net"
          class="mt-3 inline-flex items-center justify-center gap-2 text-amber-700 dark:text-amber-400 font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:ring-offset-2 rounded"
        >
          <Mail class="h-4 w-4 shrink-0" />
          contact@dinecloud.net
        </a>
      </div>

      <DialogFooter v-if="canDismiss" class="mt-6 justify-center sm:justify-center">
        <Button variant="outline" @click="emit('close')">
          {{ t("common.trialExpiredModal.close") }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
